import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import * as _ from 'lodash';
import { TMachineMaintenanceState } from '../reducers';
import * as BreakdownReasonsActions from './breakdown-reasons.actions';
import * as BreakdownReasonsSelector from './breakdown-reasons.selectors';
import {
  TFilterSelected,
  TListingFetchReason,
  TREQUEST_DELETE_BREAKDOWN_REASON,
  TREQUEST_EDIT_BREAKDOWN_REASON,
} from '@monorepo/types';
import { TLiftedFormModel, TUserPaginationOption } from '@monorepo/ui';
import { LIST_ITEMS_PER_PAGE_LIST } from '../../constants/constants';
import { LOCAL_STORAGE_UTILS } from '@monorepo/utils';

@Injectable({
  providedIn: 'root',
})
export class BreakdownReasonsFacade {
  constructor(private store: Store<TMachineMaintenanceState>) {
    this.initializePagination();
  }

  initializePagination() {
    const storedPageSize = LOCAL_STORAGE_UTILS.retrieveData('breakdownReasonsPageSize');
    if (storedPageSize && LIST_ITEMS_PER_PAGE_LIST.includes(Number(storedPageSize))) {
      this.setPagination({
        pageNo: 1,
        pageSize: parseInt(storedPageSize),
      });
    } else {
      LOCAL_STORAGE_UTILS.storeData('breakdownReasonsPageSize', LIST_ITEMS_PER_PAGE_LIST[0].toString());
      this.setPagination({
        pageNo: 1,
        pageSize: LIST_ITEMS_PER_PAGE_LIST[0],
      });
    }
  }

  resetBreakdownReasonsListInfo(fetchReason: TListingFetchReason) {
    const pageSize = Number(
      LOCAL_STORAGE_UTILS.retrieveData('breakdownReasonsPageSize') || LIST_ITEMS_PER_PAGE_LIST[0],
    );
    this.setPagination({ pageNo: 1, pageSize });
    this.fetchBreakdownReasonsList([], fetchReason);
  }

  fetchBreakdownReasonsList(selectedFilters: TFilterSelected[] = [], fetchReason: TListingFetchReason) {
    this.store.dispatch(new BreakdownReasonsActions.GetBreakdownReasonsList(selectedFilters, fetchReason));
  }

  deleteBreakdownReasonsRow(breakdownReasonIds: TREQUEST_DELETE_BREAKDOWN_REASON['breakdownReasonIds']) {
    this.store.dispatch(new BreakdownReasonsActions.DeleteBreakdownReason(breakdownReasonIds));
  }

  editBreakdownReasonsRow(payload: TREQUEST_EDIT_BREAKDOWN_REASON) {
    return this.store.dispatch(new BreakdownReasonsActions.EditBreakdownReasons(payload));
  }

  fetchBreakdownReasonsDropdown() {
    this.store.dispatch(new BreakdownReasonsActions.GetBreakdownReasonsFilterDropDownInfo());
  }

  setPagination(pagination: TUserPaginationOption) {
    LOCAL_STORAGE_UTILS.storeData('breakdownReasonsPageSize', pagination.pageSize.toString());
    this.store.dispatch(new BreakdownReasonsActions.SetBreakdownReasonsPagination(pagination));
  }

  getBreakdownReasonsDropdownInfo() {
    return this.store.pipe(select(BreakdownReasonsSelector.getBreakdownReasonsDropdownInfo));
  }

  getBreakdownReasonsListInfo() {
    return this.store.pipe(select(BreakdownReasonsSelector.getBreakdownReasonsListInfo));
  }

  getPagination() {
    return this.store.pipe(select(BreakdownReasonsSelector.getBreakdownReasonsPagination));
  }
}

export type TGetBreakdownReasonsDropdownInfo = ReturnType<BreakdownReasonsFacade['getBreakdownReasonsDropdownInfo']>;
export type TGetBreakdownReasonsListInfo = ReturnType<BreakdownReasonsFacade['getBreakdownReasonsListInfo']>;
export type TGetBreakdownReasonsPageInfo = ReturnType<BreakdownReasonsFacade['getPagination']>;
